import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import Divider from 'components/divider';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import { Circle, TriangleAlert } from 'lucide-react';
import React, { useMemo } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { Recommendation } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/recommendation-assortment.type';

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.orders.list';

type Props = {
  recommendation: Recommendation;
};

const RecommendationProductsList: React.FC<Props> = ({ recommendation }) => {
  const intl = useIntl();
  const theme = useTheme();

  const isSkipped = recommendation.autoSkipped || recommendation.manuallySkipped;

  const iconColor = useMemo(
    () => (isSkipped ? theme.palette.darkBlue.o25 : theme.palette.orange.o75),
    [recommendation]
  );

  const bodyColumns: HeadCell[] = [
    {
      id: 'product',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.body.product` }),
    },
    {
      id: 'quantity',
      align: 'right',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.quantity` }),
    },
  ];

  return (
    <Box display={'flex'} flexDirection={'column'} width={'100%'} padding={2}>
      <List dark options={bodyColumns}>
        {recommendation.products.map((line, idx) => (
          <ListRow key={idx}>
            <ListCell>
              <Box display={'flex'} gap={2} sx={{ alignItems: 'center' }}>
                {recommendation.productsOutsideCustomerAssortment.includes(line.name) ? (
                  <>
                    {line.name}
                    <Tooltip title={<FormattedMessage id={`${PAGE_NAME_ID}.body.productNotAvailableTooltipMessage`} />}>
                      <TriangleAlert fill={theme.palette.warning.main} strokeWidth={1} stroke={theme.palette.darkBlue.main} />
                    </Tooltip>
                    <Typography color={theme.palette.darkBlue.contrastText}>
                      <FormattedMessage id={`${PAGE_NAME_ID}.body.productNotAvailableMessage`} />
                    </Typography>
                  </>
                ) : (
                  line.name
                )}
              </Box>
            </ListCell>
            <ListCell align="right">
              <FormattedNumber
                value={line.quantity}
                style={`unit`}
                unit={'kilogram'}
                minimumFractionDigits={0}
                maximumFractionDigits={0}
              />
            </ListCell>
          </ListRow>
        ))}
      </List>
      <Divider
        sx={{
          m: theme.spacing(2, 0, 4, 0),
          borderColor: theme.palette.darkBlue.o25,
          width: '100%',
        }}
      />
      <Box display={'flex'} alignItems={'center'} gap={2}>
        <Circle size={8} fill={iconColor} color={iconColor} />
        <Typography>{intl.formatMessage({ id: `${PAGE_NAME_ID}.body.quantityTitle` })}</Typography>
      </Box>
    </Box>
  );
};

export default React.memo(RecommendationProductsList);
